import * as d3 from "d3";
import { AxisLeft } from "./AxisLeft";
import { AxisBottom } from "./AxisBottom";
import { useEffect, useRef} from "react";
import { InteractionData, Tooltip } from "./Tooltip";
import {UncontrolledReactSVGPanZoom} from "react-svg-pan-zoom";

const MARGIN = { top: 60, right: 60, bottom: 60, left: 80 };

export type DataPoint = {
    x: number;
    y: number;
    size: number;
    group: string;
    subGroup: string;
    cost: number;
    worldConsumption: number;
    wellProduction: number;
};

type ScatterplotProps = {
    width: number;
    height: number;
    xRange: {
        start: number;
        finish: number
    };
    yRange: {
        start: number;
        finish: number;
    };
    data: DataPoint[];
    hovered: InteractionData | null;
    setHovered: (hovered: InteractionData | null) => void;
    wellsAmount: number;
};

export const Scatterplot = ({ width, height, data, xRange, yRange, hovered, setHovered, wellsAmount }: ScatterplotProps) => {
    const boundsWidth = width - MARGIN.right - MARGIN.left;
    const boundsHeight = height - MARGIN.top - MARGIN.bottom - 40;
    // Scales
    const yScale = d3.scaleLinear().domain([yRange.start, yRange.finish]).range([boundsHeight, 0]);
    const xScale = d3
      .scaleLinear()
      .domain([xRange.start, xRange.finish])
      .range([0, boundsWidth]);
    const allGroups = data.map((d) => String(d.group));
    const colorScale = d3
      .scaleOrdinal<string>()
      .domain(allGroups)
      .range(["#e0ac2b", "#e85252", "#6689c6", "#9a6fb0", "#a53253"]);

    // Build the shapes
    const allShapes = data.map((d, i) => {
        return (
          <circle
            key={i}
            r={16}
            cx={xScale(d.x)}
            cy={yScale(d.y)}
            stroke={colorScale(d.group)}
            strokeWidth={3}
            fill={colorScale(d.group)}
            fillOpacity={0.6}
            onMouseEnter={() =>
              setHovered({
                  xPos: xScale(d.x),
                  x: d.x,
                  y: d.y,
                  yPos: yScale(d.y),
                  name: d.subGroup,
                  cost: d.cost,
                  worldConsumption: d.worldConsumption,
                  wellProduction: d.wellProduction,
              })
            }
            onMouseLeave={() => setHovered(null)}
          />
        );
    });

    const Viewer = useRef(null);

    useEffect(() => {
        //@ts-ignore
        Viewer.current.fitToViewer();
    }, [width, height]);

    return (
      <div style={{position: "relative", display: "flex"}}>
          <div style={{display: "flex", width: "100%"}}>
              <UncontrolledReactSVGPanZoom
                ref={Viewer}
                width={width}
                height={height}
                detectAutoPan={false}
              >
                  <svg width={width} height={height}>
                      <g
                        width={boundsWidth}
                        height={boundsHeight}
                        transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
                      >
                          {/* Y axis */}
                          <AxisLeft yScale={yScale} pixelsPerTick={40} width={boundsWidth}/>

                          {/* X axis, use an additional translation to appear at the bottom */}
                          <g transform={`translate(0, ${boundsHeight})`}>
                              <AxisBottom
                                xScale={xScale}
                                pixelsPerTick={40}
                                height={boundsHeight}
                              />
                          </g>
                        {/*<text*/}
                        {/*  x={xScale(xRange.finish * 0.92)}*/}
                        {/*  y={yScale(yRange.finish * 0.95)}*/}
                        {/*  textAnchor="end"// Adjust the translation values to position the text precisely*/}
                        {/*  style={{ fontSize: 18 }}*/}
                        {/*>*/}
                        {/*  x{wellsAmount}*/}
                        {/*</text>*/}
                          <line
                            x1={xScale(0)} y1={yScale(0)}
                            x2={xScale(xRange.finish)} y2={yScale(yRange.finish)}
                            stroke="black"
                            strokeWidth="2"
                          />
                          {/* Circles */}
                          {allShapes}
                          {data.sort((a, b) => a.x - b.x).map((shape, index) => (
                            <text
                              x={xScale(shape.x)} // Use the x value of the shape
                              y={yScale(shape.y - 0.2)} // Add spacing based on index
                              key={shape.subGroup} // Assuming each shape has a unique id
                              style={{textAnchor: 'middle', pointerEvents: 'none', fontSize: 16}} // Center text horizontally
                            >
                                {shape.subGroup}
                            </text>
                          ))}
                      </g>
                  </svg>
              </UncontrolledReactSVGPanZoom>

              {/* Tooltip */}
              <div
                style={{
                    width: boundsWidth,
                    height: boundsHeight,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    pointerEvents: "none",
                    marginLeft: MARGIN.left,
                    marginTop: MARGIN.top,
                }}
              >
                  <Tooltip interactionData={hovered}/>
              </div>
          </div>
      </div>
    );
};